import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { http, formatApiError } from "@/lib/api";
import { Loader2, ArrowRight, CheckCircle2, KeyRound, UserRound, Mail, ShieldCheck } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MzA4NzQzOHww&ixlib=rb-4.1.0&q=85";
const LOGO = "https://customer-assets.emergentagent.com/job_sales-tracker-1019/artifacts/0i835tsb_f293e0f8-0ee3-473a-b4f8-1de12450a9c6.png";

export default function Register() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    invitation_code: params.get("code") || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const c = params.get("code");
    if (c) setForm((f) => ({ ...f, invitation_code: c }));
  }, [params]);

  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await http.post("/auth/register", {
        ...form,
        invitation_code: form.invitation_code.trim().toUpperCase(),
      });
      setUser(data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center px-6 sm:px-16 py-16">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <img src={LOGO} alt="Immo Varois" className="h-24 w-auto object-contain" />
          </div>

          <p className="label-eyebrow mb-3">Créer mon compte</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-slate-900 leading-[1.05]">
            Bienvenue<br />
            <span className="text-primary">dans l'équipe.</span>
          </h1>
          <p className="mt-5 text-slate-600 text-base leading-relaxed">
            Complétez ces informations pour créer votre espace négociateur. Le code d'invitation vous a été communiqué par votre direction.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Nom complet</Label>
              <div className="relative">
                <UserRound className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  required
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="h-12 pl-9 text-base"
                  placeholder="Ex : Marie Dupont"
                  data-testid="register-name"
                  autoComplete="name"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Email professionnel</Label>
              <div className="relative">
                <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="h-12 pl-9 text-base"
                  placeholder="vous@agence.fr"
                  data-testid="register-email"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Mot de passe (6 min.)</Label>
              <div className="relative">
                <KeyRound className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  required
                  type="password"
                  minLength={6}
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  className="h-12 pl-9 text-base"
                  placeholder="Choisissez un mot de passe"
                  data-testid="register-password"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Code d'invitation</Label>
              <div className="relative">
                <ShieldCheck className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  required
                  value={form.invitation_code}
                  onChange={(e) => set("invitation_code", e.target.value.toUpperCase())}
                  className="h-12 pl-9 text-base font-mono tracking-wider"
                  placeholder="IMMO-XXXXXX"
                  data-testid="register-code"
                />
              </div>
              <p className="text-xs text-slate-500">Fourni par votre direction.</p>
            </div>

            {error ? (
              <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5" data-testid="register-error">
                {error}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-base gap-2 group"
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" /> Créer mon compte
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>

            <div className="text-sm text-center text-slate-500 pt-2">
              Vous avez déjà un compte ?{" "}
              <Link to="/login" className="text-primary font-semibold hover:underline">Se connecter</Link>
            </div>
          </form>
        </div>
      </div>

      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG} alt="Architecture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/70 via-slate-900/30 to-transparent" />
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="label-eyebrow text-white/70 mb-3">Rejoignez l'équipe</div>
          <div className="font-display font-bold text-4xl leading-tight max-w-md">
            Votre activité, vos ventes, votre carte de prospection. Le tout en un espace clair.
          </div>
        </div>
      </div>
    </div>
  );
}
